'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// ----------------------------------------------------------------------
// Workspace (active Client Tier 1) context.
//
// Some users do NOT have a `clientTier1` attached to their role, but their
// role permits them to view/select Tier-1 clients (see WorkspacesPopover).
// In that case we need a single source of truth for the currently-selected
// Tier-1 client across the whole dashboard.
//
// Pages should read the active id via `useActiveClientTier1Id(fallback)`,
// passing the session-bound id (when present) as the fallback.
// ----------------------------------------------------------------------

const STORAGE_KEY = 'hokela.activeClientTier1';

/**
 * Clear any persisted workspace selection. Safe to call from non-React
 * contexts (e.g. logout handlers, session-expiry interceptors).
 */
export function clearPersistedWorkspace() {
  try {
    if (typeof window !== 'undefined') window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

export type ActiveWorkspace = {
  id: string;
  name?: string;
  logo?: string;
} | null;

type WorkspaceContextValue = {
  workspace: ActiveWorkspace;
  setWorkspace: (w: ActiveWorkspace) => void;
  clearWorkspace: () => void;
};

const WorkspaceContext = createContext<WorkspaceContextValue | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const [workspace, setWorkspaceState] = useState<ActiveWorkspace>(null);

  // Hydrate from localStorage on mount (client-only).
  useEffect(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
      if (raw) setWorkspaceState(JSON.parse(raw));
    } catch {
      /* noop */
    }
  }, []);

  const setWorkspace = useCallback((w: ActiveWorkspace) => {
    setWorkspaceState(w);
    try {
      if (w) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(w));
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
  }, []);

  const clearWorkspace = useCallback(() => setWorkspace(null), [setWorkspace]);

  const value = useMemo(
    () => ({ workspace, setWorkspace, clearWorkspace }),
    [workspace, setWorkspace, clearWorkspace],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    // Provider missing — return a no-op so consumers don't crash.
    return {
      workspace: null,
      setWorkspace: () => {},
      clearWorkspace: () => {},
    };
  }
  return ctx;
}

/**
 * Returns the active Tier-1 client id to use for queries.
 *
 * Priority:
 *   1. If the current session already has a Tier-1 id bound to the user's
 *      role (`fallbackId`), that ALWAYS wins. Any previously-persisted
 *      workspace from another account is auto-cleared so a re-login as a
 *      different user can't pick up the prior selection.
 *   2. Otherwise (no session-bound id), the workspace selected via
 *      WorkspacesPopover (persisted in localStorage) is used.
 */
export function useActiveClientTier1Id(fallbackId?: string | null): string | undefined {
  const { workspace, clearWorkspace } = useWorkspace();

  useEffect(() => {
    // If the freshly-authenticated session brings its own Tier-1 id and it
    // differs from what we have cached, drop the stale selection.
    if (fallbackId && workspace?.id && workspace.id !== fallbackId) {
      clearWorkspace();
    }
  }, [fallbackId, workspace?.id, clearWorkspace]);

  if (fallbackId) return fallbackId;
  return workspace?.id || undefined;
}

